import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import { apiRequest } from "../../../core/common/api";
import { canManageUsers } from "../../../core/common/adminPermissions";

const emptyForm = {
  name: "",
  email: "",
  phone: "",
  sponsorId: "",
  buyerId: "",
};

const SaleUserAdd = () => {
  const navigate = useNavigate();
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!canManageUsers()) {
      navigate("/sale-users", { replace: true });
    }
  }, [navigate]);

  const updateForm = (updates: Partial<typeof emptyForm>) => {
    setForm((current) => ({ ...current, ...updates }));
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();

    if (!form.name.trim() || !form.email.trim() || !form.phone.trim()) {
      Swal.fire("Validation Error", "Name, email and phone are required", "error");
      return;
    }
    if (!form.buyerId.trim()) {
      Swal.fire(
        "Validation Error",
        "Buyer member ID is required so income can credit a registered user",
        "error",
      );
      return;
    }

    setSaving(true);
    try {
      const result = await apiRequest("/sale-users", "POST", {
        name: form.name.trim(),
        email: form.email.trim(),
        phone: form.phone.trim(),
        sponsorId: form.sponsorId.trim(),
        buyerId: form.buyerId.trim(),
      });

      if (!result.success) {
        throw new Error(result.message || "Unable to create sale user");
      }

      await Swal.fire({
        title: "Sale User Created",
        html: `<p>Member ID: <strong>${result.user?.memberId || "—"}</strong></p>               <p>Buyer: <strong>${result.user?.buyerId || "—"}</strong></p>`,
        icon: "success",
      });

      navigate(result.user?._id ? `/sale-users/${result.user._id}` : "/sale-users");
    } catch (error: any) {
      Swal.fire(
        "Create Failed",
        error.message || "Unable to create sale user",
        "error",
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="detail-page">
      <div className="page-hdr">
        <div className="page-hdr-left">
          <Link to="/sale-users" className="btn-sm btn-outline back-link">
            ← Back to Sale Users
          </Link>
          <div className="page-eyebrow">User Management</div>
          <h1 className="page-title">
            Add <em>Sale User</em>
          </h1>
          <div className="page-sub">
            Creates a placement-tree member in saleusermodel. Income is credited to
            the buyer member ID on usermodel.
          </div>
        </div>
        <div className="page-hdr-right">
          <button
            type="submit"
            form="sale-user-add-form"
            className="btn-sm btn-primary"
            disabled={saving}
          >
            {saving ? "Creating..." : "Create Sale User"}
          </button>
        </div>
      </div>

      <form id="sale-user-add-form" onSubmit={handleSubmit} className="page-stack">
        <section className="table-card">
          <div className="table-head">
            <h2>Sale Account Details</h2>
            <p>
              Placement fills the next open company-tree slot. Buyer ID must be an
              existing registered member.
            </p>
          </div>
          <div className="admin-card-body admin-form-grid">
            <div className="form-field">
              <label className="modal-label">Full Name</label>
              <input
                className="admin-input"
                value={form.name}
                onChange={(event) => updateForm({ name: event.target.value })}
                required
              />
            </div>
            <div className="form-field">
              <label className="modal-label">Email Address</label>
              <input
                type="email"
                className="admin-input"
                value={form.email}
                onChange={(event) => updateForm({ email: event.target.value })}
                required
              />
            </div>
            <div className="form-field">
              <label className="modal-label">Phone Number</label>
              <input
                className="admin-input"
                value={form.phone}
                onChange={(event) => updateForm({ phone: event.target.value })}
                required
              />
            </div>
            <div className="form-field">
              <label className="modal-label">Buyer Member ID (income)</label>
              <input
                className="admin-input"
                value={form.buyerId}
                onChange={(event) => updateForm({ buyerId: event.target.value })}
                placeholder="RW960001"
                required
              />
            </div>
            <div className="form-field">
              <label className="modal-label">Sponsor ID (optional)</label>
              <input
                className="admin-input"
                value={form.sponsorId}
                onChange={(event) => updateForm({ sponsorId: event.target.value })}
                placeholder="RW960001"
              />
            </div>
          </div>
        </section>

        <div className="detail-footer">
          <Link to="/sale-users" className="btn-sm btn-outline">
            Cancel
          </Link>
          <div className="detail-footer-right">
            <button
              type="submit"
              className="btn-sm btn-primary"
              disabled={saving}
            >
              {saving ? "Creating..." : "Create Sale User"}
            </button>
          </div>
        </div>
      </form>
    </div>
  );
};

export default SaleUserAdd;
